if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.navigationInitialized = false;
        this.touchStartX = null;
      }

      hide() {
        super.hide();
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      }

      show(opener) {
        super.show(opener);
        this.initNavigation();
        this.showActiveMedia();
      }

      initNavigation() {
        if (this.navigationInitialized) return;

        this.ensureNavigationStyles();

        const dialog = this.querySelector('.product-media-modal__dialog');
        if (!dialog) return;

        const labels = this.getNavigationLabels();
        const previousButton = this.createNavigationButton('prev', labels.previous);
        const nextButton = this.createNavigationButton('next', labels.next);

        dialog.append(previousButton, nextButton);

        const stopModalPointerClose = (event) => event.stopPropagation();
        ['pointerdown', 'pointerup', 'mousedown', 'mouseup'].forEach((eventName) => {
          previousButton.addEventListener(eventName, stopModalPointerClose);
          nextButton.addEventListener(eventName, stopModalPointerClose);
        });

        previousButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.showAdjacentMedia(-1);
        });
        nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.showAdjacentMedia(1);
        });

        this.addEventListener('keydown', this.onKeydown.bind(this));
        this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });

        this.navigationInitialized = true;
      }

      ensureNavigationStyles() {
        if (document.getElementById('arca-product-modal-navigation-styles')) return;

        const style = document.createElement('style');
        style.id = 'arca-product-modal-navigation-styles';
        style.textContent = `
          .product-media-modal__content > [data-media-id]:not(.active) {
            display: none !important;
          }

          .product-media-modal__content > [data-media-id].active {
            display: block !important;
          }

          .product-media-modal__nav {
            position: fixed;
            top: 50%;
            z-index: 5;
            width: 5.4rem;
            height: 5.4rem;
            display: flex;
            align-items: center;
            justify-content: center;
            transform: translateY(-50%);
            padding: 0;
            border: 1px solid rgba(var(--color-foreground), 0.16);
            border-radius: 50%;
            background: rgba(var(--color-background), 0.88);
            color: rgb(var(--color-foreground));
            box-shadow: 0 0.8rem 2.4rem rgba(0, 0, 0, 0.06);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
            cursor: pointer;
            touch-action: manipulation;
            user-select: none;
            -webkit-user-select: none;
            opacity: 0.9;
            transition: background 180ms ease, border-color 180ms ease, opacity 180ms ease, box-shadow 180ms ease;
          }

          .product-media-modal__nav--prev {
            left: 2.4rem;
          }

          .product-media-modal__nav--next {
            right: 2.4rem;
          }

          .product-media-modal__nav svg {
            width: 1.75rem;
            height: 1.75rem;
            display: block;
            pointer-events: none;
          }

          .product-media-modal__nav--next svg {
            transform: rotate(180deg);
          }

          .product-media-modal__nav:hover {
            background: rgb(var(--color-background));
            border-color: rgba(var(--color-foreground), 0.34);
            box-shadow: 0 1rem 2.8rem rgba(0, 0, 0, 0.08);
            opacity: 1;
          }

          .product-media-modal__nav:active {
            background: rgba(var(--color-background), 0.96);
            border-color: rgba(var(--color-foreground), 0.44);
          }

          .product-media-modal__nav:focus-visible {
            outline: 1px solid rgb(var(--color-foreground));
            outline-offset: 4px;
            opacity: 1;
          }

          .product-media-modal__nav[hidden] {
            display: none !important;
          }

          @media screen and (min-width: 1200px) {
            .product-media-modal__nav--prev {
              left: 3.2rem;
            }

            .product-media-modal__nav--next {
              right: 3.2rem;
            }
          }

          @media screen and (max-width: 749px) {
            .product-media-modal__nav {
              width: 4.2rem;
              height: 4.2rem;
              border-radius: 50%;
              background: rgba(var(--color-background), 0.9);
              box-shadow: 0 0.5rem 1.8rem rgba(0, 0, 0, 0.05);
              backdrop-filter: none;
              -webkit-backdrop-filter: none;
            }

            .product-media-modal__nav--prev {
              left: 0.8rem;
            }

            .product-media-modal__nav--next {
              right: 0.8rem;
            }

            .product-media-modal__nav svg {
              width: 1.6rem;
              height: 1.6rem;
            }
          }
        `;
        document.head.appendChild(style);
      }

      createNavigationButton(direction, label) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `product-media-modal__nav product-media-modal__nav--${direction}`;
        button.setAttribute('aria-label', label);
        button.innerHTML = `
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M15 5L8 12L15 19" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        `;
        return button;
      }

      getNavigationLabels() {
        const lang = (document.documentElement.lang || 'en').toLowerCase();
        if (lang.startsWith('de')) return { previous: 'Vorheriges Bild', next: 'Nächstes Bild' };
        if (lang.startsWith('it')) return { previous: 'Immagine precedente', next: 'Immagine successiva' };
        return { previous: 'Previous image', next: 'Next image' };
      }

      getMediaItems() {
        const items = Array.from(
          this.querySelectorAll('.product-media-modal__content > [data-media-id]')
        );
        const openedMediaId = this.openedBy?.getAttribute('data-media-id');

        return items.filter((item, index) => {
          const isVariantImage = item.classList.contains('product__media-item--variant');
          return !isVariantImage || index === 0 || item.dataset.mediaId === openedMediaId;
        });
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        if (!mediaId) return;
        this.activateMedia(mediaId);
      }

      activateMedia(mediaId) {
        const mediaItems = Array.from(
          this.querySelectorAll('.product-media-modal__content > [data-media-id]')
        );
        const activeMedia = mediaItems.find((item) => item.dataset.mediaId === mediaId);
        if (!activeMedia) return;

        mediaItems.forEach((element) => element.classList.remove('active'));
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        activeMedia.classList.add('active');

        const container = this.querySelector('.product-media-modal__content');
        if (container) {
          container.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }

        this.updateNavigation();
      }

      showAdjacentMedia(direction) {
        const items = this.getMediaItems();
        if (items.length <= 1) return;

        let currentIndex = items.findIndex((item) => item.classList.contains('active'));
        if (currentIndex < 0) currentIndex = 0;

        const nextIndex = (currentIndex + direction + items.length) % items.length;
        this.activateMedia(items[nextIndex].dataset.mediaId);
      }

      updateNavigation() {
        const hasMultipleMedia = this.getMediaItems().length > 1;
        this.querySelector('.product-media-modal__nav--prev')?.toggleAttribute('hidden', !hasMultipleMedia);
        this.querySelector('.product-media-modal__nav--next')?.toggleAttribute('hidden', !hasMultipleMedia);
      }

      onKeydown(event) {
        if (!this.hasAttribute('open')) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showAdjacentMedia(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showAdjacentMedia(1);
        }
      }

      onTouchStart(event) {
        if (event.touches.length === 1) this.touchStartX = event.touches[0].clientX;
      }

      onTouchEnd(event) {
        if (this.touchStartX === null || !event.changedTouches.length) return;

        const deltaX = event.changedTouches[0].clientX - this.touchStartX;
        this.touchStartX = null;

        if (Math.abs(deltaX) < 45) return;
        this.showAdjacentMedia(deltaX > 0 ? -1 : 1);
      }
    }
  );
}
